import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import useTranslator from './useTranslator'
import useSelectionCapture from './useSelectionCapture'

const TranslatorContext = createContext(null)

/**
 * Singleton state of the floating translator (#2892), mounted once in the shell (MainContainer).
 *
 * The translation logic lives here and not in the panel so that minimizing (which unmounts the
 * panel) or changing route never loses text, pair, result or an in-flight request (RN3.1).
 *
 * ui.status: 'closed' | 'open' | 'minimized'
 * ui.position: {x, y} | null (null = default bottom-right corner). Kept across close (spec §4).
 * ui.bubblePosition: {x, y} | null — the minimized bubble's own place (null = bottom-left corner).
 * Independent from the panel: minimizing parks the bubble in its corner instead of leaving it where
 * the panel's top-left was, and dragging the bubble never moves the panel (PO feedback 2026-09-09).
 * ui.badge: a translation settled while minimized; cleared on open.
 */
export const TranslatorProvider = ({ children }) => {
  const [status, setStatus] = useState('closed')
  const [position, setPosition] = useState(null)
  const [bubblePosition, setBubblePosition] = useState(null)
  const [badge, setBadge] = useState(false)
  const statusRef = useRef(status)
  statusRef.current = status
  // The panel's root element; selections inside it are never captured (spec §5).
  const widgetRef = useRef(null)

  const onSettled = useCallback(() => {
    if (statusRef.current === 'minimized') setBadge(true)
  }, [])

  // Catalogue and health load on the closed → open edge only: minimized keeps `active` true.
  const translator = useTranslator({ active: status !== 'closed', onSettled })

  useSelectionCapture({
    enabled: status === 'open' && !translator.translating,
    widgetRef,
    onCapture: translator.setText
  })

  const open = useCallback(() => {
    setBadge(false)
    setStatus('open')
  }, [])
  const minimize = useCallback(() => setStatus('minimized'), [])
  const { reset } = translator
  // RN3: closing wipes content and aborts; the position is a UI preference and survives.
  const close = useCallback(() => {
    reset()
    setStatus('closed')
  }, [reset])

  const value = useMemo(() => ({
    ui: { status, position, bubblePosition, badge },
    open,
    minimize,
    close,
    setPosition,
    setBubblePosition,
    widgetRef,
    translator
  }), [status, position, bubblePosition, badge, open, minimize, close, translator])

  return <TranslatorContext.Provider value={value}>{children}</TranslatorContext.Provider>
}

/**
 * Access to the floating translator. Must be rendered under <TranslatorProvider>.
 */
export const useTranslatorWidget = () => {
  const context = useContext(TranslatorContext)
  if (!context) throw new Error('useTranslatorWidget must be used inside <TranslatorProvider>')
  return context
}
